/**
 * A local mirror of an in-progress enrollment form, so a failed autosave cannot cost the
 * parent their typing.
 *
 * The server draft is the real record. This is only a safety net for the window between
 * a change and a successful save - autosave is debounced 30 seconds, and if the network
 * drops, the token expires, or the tab dies in that window, everything typed since the
 * last good save is gone. On a form with 140 required answers that is a lot to retype.
 *
 * How it behaves:
 *  - written on change (the caller debounces), synchronously, so a crash cannot beat it
 *  - CLEARED as soon as a save succeeds, so a mirror existing at load time means exactly
 *    one thing: there were changes that never reached the server
 *  - never restored automatically. The parent is asked, because silently replacing server
 *    data with older local data would be its own kind of data loss.
 *
 * Signature images are deliberately not mirrored. They are base64 data URIs, easily
 * hundreds of KB each, and would blow the ~5MB localStorage quota - taking the rest of
 * the backup with them. They are dropped and flagged, so the parent is told to re-sign
 * rather than being handed a form that looks complete but is not.
 */

const PREFIX = 'b2s_draft_backup_';
const VERSION = 1;

function keyFor(studentIntakeId) {
  return `${PREFIX}${studentIntakeId || 'unknown'}`;
}

function looksLikeSignature(value) {
  if (typeof value === 'string') return value.startsWith('data:');
  if (value && typeof value === 'object' && typeof value.image === 'string') {
    return value.image.startsWith('data:');
  }
  return false;
}

/** Strip the heavy signature payloads, remembering which fields had one. */
function stripSignatures(formValues) {
  const out = {};
  const droppedSignatureFields = [];
  for (const [k, v] of Object.entries(formValues || {})) {
    if (looksLikeSignature(v)) {
      droppedSignatureFields.push(k);
      continue;
    }
    out[k] = v;
  }
  return { values: out, droppedSignatureFields };
}

export function saveLocalDraft(studentIntakeId, formValues, extras = {}) {
  if (!studentIntakeId || !formValues || Object.keys(formValues).length === 0) return false;
  try {
    const { values, droppedSignatureFields } = stripSignatures(formValues);
    const payload = {
      version: VERSION,
      studentIntakeId,
      savedAt: new Date().toISOString(),
      droppedSignatureFields,
      values,
      ...extras
    };
    window.localStorage.setItem(keyFor(studentIntakeId), JSON.stringify(payload));
    return true;
  } catch (e) {
    // Quota exceeded, private mode, storage disabled - the server draft still applies.
    return false;
  }
}

export function readLocalDraft(studentIntakeId) {
  if (!studentIntakeId) return null;
  try {
    const raw = window.localStorage.getItem(keyFor(studentIntakeId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== VERSION || !parsed.values) return null;
    if (Object.keys(parsed.values).length === 0) return null;
    return parsed;
  } catch (e) {
    return null;
  }
}

export function clearLocalDraft(studentIntakeId) {
  if (!studentIntakeId) return;
  try {
    window.localStorage.removeItem(keyFor(studentIntakeId));
  } catch (e) { /* nothing to do */ }
}

/** "3 minutes ago" - so the parent can judge whether the backup is the version they want. */
export function describeAge(isoString) {
  const then = new Date(isoString);
  if (Number.isNaN(then.getTime())) return 'earlier';
  const seconds = Math.max(0, Math.round((Date.now() - then.getTime()) / 1000));
  if (seconds < 60) return 'less than a minute ago';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

/**
 * Which fields the local copy would change, so the prompt can say how much is at stake
 * rather than asking the parent to gamble.
 */
export function countRecoverableChanges(localValues, serverValues) {
  if (!localValues) return 0;
  const server = serverValues || {};
  let n = 0;
  for (const [k, v] of Object.entries(localValues)) {
    const a = v == null ? '' : (typeof v === 'object' ? JSON.stringify(v) : String(v)).trim();
    const b = server[k] == null ? '' : (typeof server[k] === 'object' ? JSON.stringify(server[k]) : String(server[k])).trim();
    if (a !== '' && a !== b) n += 1;
  }
  return n;
}
